import React, { useState, useMemo, useCallback, useRef } from 'react';
import { useMapDevices } from '../hooks/useMapDevices';
import MapKpiStrip from '../components/map/MapKpiStrip';
import DeviceListPanel from '../components/map/DeviceListPanel';
import DeviceMap from '../components/map/DeviceMap';
import DeviceDetailDrawer from '../components/map/DeviceDetailDrawer';
import '../DeviceMap.css';

const ChevronLeft = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor"
    strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <polyline points="15 18 9 12 15 6" />
  </svg>
);
const ChevronRight = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor"
    strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <polyline points="9 18 15 12 9 6" />
  </svg>
);

const FitIcon = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor"
    strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M15 3h6v6M9 21H3v-6M21 3l-7 7M3 21l7-7" />
  </svg>
);
const RefreshIcon = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor"
    strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <polyline points="23 4 23 10 17 10" />
    <path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10" />
  </svg>
);
const PlusIcon = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor"
    strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <line x1="12" y1="5" x2="12" y2="19" /><line x1="5" y1="12" x2="19" y2="12" />
  </svg>
);
const MinusIcon = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor"
    strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <line x1="5" y1="12" x2="19" y2="12" />
  </svg>
);

export default function DeviceMapPage({ st, lightbugLive, darkMode }) {
  const { devices, mappableDevices, loading, counts, refetch, lastSync } = useMapDevices(st, lightbugLive);

  const [selectedDeviceId, setSelectedDeviceId] = useState(null);
  const [panelVisible, setPanelVisible] = useState(true);
  const [activeFilter, setActiveFilter] = useState('all');
  const [locationFilter, setLocationFilter] = useState('all');
  const [batchFilter, setBatchFilter] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');

  // The Leaflet instance, so the control cluster can drive the map's own zoom
  // rather than reimplementing it. Leaflet's native zoom control stays in the
  // DOM (visually hidden, still focusable) and keeps its keyboard and ARIA
  // behaviour; these buttons are a styled front end onto the same methods.
  const mapRef = useRef(null);

  // Distinct site locations present among devices (sorted)
  const locationOptions = useMemo(() => {
    const set = new Set();
    devices.forEach(d => { if (d.location) set.add(d.location); });
    return Array.from(set).sort((a, b) => a.localeCompare(b));
  }, [devices]);

  // Distinct import batches present among devices. Natural sort so numeric batch
  // references don't order as 1, 10, 2.
  const batchOptions = useMemo(() => {
    const set = new Set();
    devices.forEach(d => { if (d.importBatch) set.add(d.importBatch); });
    return Array.from(set).sort((a, b) => a.localeCompare(b, undefined, { numeric: true }));
  }, [devices]);
  const [fitTrigger, setFitTrigger] = useState(0);
  const [clusterEnabled, setClusterEnabled] = useState(false);

  // Filter devices
  const filtered = useMemo(() => {
    let result = devices;

    // Status / type filter
    if (activeFilter === 'online') result = result.filter(d => d.status === 'online');
    else if (activeFilter === 'offline') result = result.filter(d => d.status === 'offline');
    else if (activeFilter === 'idle') result = result.filter(d => d.status === 'idle');
    else if (activeFilter === 'RH2') result = result.filter(d => d.type === 'RH2');
    else if (activeFilter === 'RH1') result = result.filter(d => d.type === 'RH1');
    else if (activeFilter === 'Teltonika') result = result.filter(d => d.type === 'Teltonika');

    // Site location filter
    if (locationFilter !== 'all') {
      result = result.filter(d => d.location === locationFilter);
    }

    // Import batch filter
    if (batchFilter !== 'all') {
      result = result.filter(d => d.importBatch === batchFilter);
    }

    // Search
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      result = result.filter(d =>
        (d.name || '').toLowerCase().includes(q) ||
        (d.serial || '').toLowerCase().includes(q) ||
        (d.client || '').toLowerCase().includes(q) ||
        (d.location || '').toLowerCase().includes(q) ||
        (d.importBatch || '').toLowerCase().includes(q)
      );
    }

    return result;
  }, [devices, activeFilter, locationFilter, batchFilter, searchQuery]);

  // Counts for the KPI strip. Scoped to batch / location / search so the tiles describe the
  // set you are actually looking at — but deliberately NOT to the status filter, which the
  // tiles themselves set: scoping by it would zero out every tile except the active one.
  const scopedCounts = useMemo(() => {
    let result = devices;
    if (locationFilter !== 'all') result = result.filter(d => d.location === locationFilter);
    if (batchFilter !== 'all') result = result.filter(d => d.importBatch === batchFilter);
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      result = result.filter(d =>
        (d.name || '').toLowerCase().includes(q) ||
        (d.serial || '').toLowerCase().includes(q) ||
        (d.client || '').toLowerCase().includes(q) ||
        (d.location || '').toLowerCase().includes(q) ||
        (d.importBatch || '').toLowerCase().includes(q)
      );
    }
    // A device-type chip (RH2/RH1/Teltonika) narrows the fleet too, and unlike the status
    // filters it doesn't compete with the tiles, so it is applied here.
    if (['RH2', 'RH1', 'Teltonika'].includes(activeFilter)) {
      result = result.filter(d => d.type === activeFilter);
    }
    return {
      total: result.length,
      online: result.filter(d => d.status === 'online').length,
      idle: result.filter(d => d.status === 'idle').length,
      offline: result.filter(d => d.status === 'offline').length,
    };
  }, [devices, locationFilter, batchFilter, searchQuery, activeFilter]);

  const isScoped = locationFilter !== 'all' || batchFilter !== 'all' || !!searchQuery.trim()
    || ['RH2', 'RH1', 'Teltonika'].includes(activeFilter);

  // Filtered devices that have GPS — for map markers
  const filteredMapDevices = useMemo(() => {
    const filteredIds = new Set(filtered.map(d => d.id));
    return mappableDevices.filter(d => filteredIds.has(d.id));
  }, [filtered, mappableDevices]);

  const selectedDevice = useMemo(
    () => devices.find(d => d.id === selectedDeviceId) || null,
    [devices, selectedDeviceId]
  );

  const handleSelectDevice = useCallback((id) => {
    setSelectedDeviceId(prev => prev === id ? null : id);
  }, []);

  const handleCloseDrawer = useCallback(() => {
    setSelectedDeviceId(null);
  }, []);

  const handleFitAll = useCallback(() => {
    setFitTrigger(t => t + 1);
  }, []);

  const handleRefresh = useCallback(() => {
    refetch();
  }, [refetch]);

  const handleZoomIn = useCallback(() => {
    if (mapRef.current) mapRef.current.zoomIn();
  }, []);

  const handleZoomOut = useCallback(() => {
    if (mapRef.current) mapRef.current.zoomOut();
  }, []);

  const syncTimeStr = lastSync
    ? lastSync.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit', second: '2-digit' })
    : '';

  if (loading) {
    return (
      <div className="device-map-page">
        <div className="dm-loading">Loading map data…</div>
      </div>
    );
  }

  return (
    <div className="device-map-page">
      <div className={`device-map-body${panelVisible ? '' : ' panel-hidden'}${selectedDevice ? ' drawer-open' : ''}`}>
        {/* Map */}
        <DeviceMap
          devices={filteredMapDevices}
          selectedDeviceId={selectedDeviceId}
          onSelectDevice={handleSelectDevice}
          fitTrigger={fitTrigger}
          clusterEnabled={clusterEnabled}
          darkMode={darkMode}
          mapRef={mapRef}
        />

        {/* Left panel */}
        <DeviceListPanel
          devices={filtered}
          mappableCount={filteredMapDevices.length}
          selectedDeviceId={selectedDeviceId}
          onSelectDevice={handleSelectDevice}
          searchQuery={searchQuery}
          onSearchChange={setSearchQuery}
          activeFilter={activeFilter}
          onFilterChange={setActiveFilter}
          locationFilter={locationFilter}
          onLocationChange={setLocationFilter}
          locationOptions={locationOptions}
          batchFilter={batchFilter}
          onBatchChange={setBatchFilter}
          batchOptions={batchOptions}
          panelVisible={panelVisible}
        />

        {/* KPI strip */}
        <MapKpiStrip counts={scopedCounts} fleetTotal={counts.total} isScoped={isScoped} panelVisible={panelVisible} activeFilter={activeFilter} onFilterChange={setActiveFilter} />

        {/* Panel toggle */}
        <button
          type="button"
          className="icon-btn-sq dm-panel-toggle"
          onClick={() => setPanelVisible(v => !v)}
          aria-label={panelVisible ? 'Collapse device panel' : 'Expand device panel'}
          aria-expanded={panelVisible}
        >
          {panelVisible ? <ChevronLeft /> : <ChevronRight />}
        </button>

        {/* Map controls (bottom right) */}
        <div className="dm-map-controls" role="group" aria-label="Map controls">
          <button type="button" className="icon-btn-sq" onClick={handleZoomIn} aria-label="Zoom in">
            <PlusIcon />
          </button>
          <button type="button" className="icon-btn-sq" onClick={handleZoomOut} aria-label="Zoom out">
            <MinusIcon />
          </button>
          <button type="button" className="icon-btn-sq has-label" onClick={handleFitAll} title="Fit all devices">
            <FitIcon /> Fit all
          </button>
          <button
            type="button"
            className={`icon-btn-sq has-label${clusterEnabled ? ' active' : ''}`}
            onClick={() => setClusterEnabled(c => !c)}
            title="Toggle clusters"
            aria-pressed={clusterEnabled}
          >
            Clusters
          </button>
          <button type="button" className="icon-btn-sq has-label" onClick={handleRefresh} title="Refresh data">
            <RefreshIcon /> Refresh
          </button>
        </div>

        {/* Sync banner (bottom left) */}
        <div className="surface-float float-sm dm-sync-banner">
          <span className="dm-sync-dot" />
          <span>Live</span>
          <span className="dm-sync-label">last sync {syncTimeStr}</span>
        </div>

        {/* Detail drawer */}
        <DeviceDetailDrawer device={selectedDevice} onClose={handleCloseDrawer} />
      </div>
    </div>
  );
}
